import { useMenuContext } from '#pplx/frontend/aether/components/internal/menu/useMenuContext.js';
/**
 * Hook for accessing menu focus behavior from within menu content. It returns focusFirstMenuItem
 * when called inside a DropdownMenu context, and null outside of one.
 *
 * @category hooks
 * @keywords
 *   - use
 *   - menu
 *   - hooks
 *   - focus
 *   - first
 *   - item
 *   - focusfirstmenuitem
 *   - inside
 *   - content
 *   - context
 *   - null
 * @whenToUse Use useMenu only inside custom DropdownMenu content that needs to imperatively focus
 *   the first registered menu item. It is not a general DropdownMenu controller; use the
 *   DropdownMenu component API for opening, closing, positioning, item structure, submenus, and
 *   mobile behavior.
 * @guidelineDo
 *   - Use useMenu from components rendered inside DropdownMenu content when you need to move focus
 *     to the first registered menu item
 *   - Handle the null return value when a component can render outside a DropdownMenu context
 * @guidelineDont
 *   - Don't use useMenu to control menu open or close state; it only exposes focusFirstMenuItem
 *   - Don't use useMenu for positioning, submenu behavior, or mobile bottom-sheet state
 * @accessibility
 *   - focusFirstMenuItem delegates to the DropdownMenu item registry so custom menu content can
 *     move keyboard focus into the menu item list
 *   - The hook does not manage ARIA attributes, focus return, Escape handling, arrow-key
 *     navigation, or menu open/close state
 * @relatedComponents
 *   - menu
 * @returns Object containing focusFirstMenuItem function, or null if called outside a Menu
 * @example
 *   ```tsx
 *   function CustomMenuContent() {
 *     const { focusFirstMenuItem } = useMenu();
 *   }
 *   ```
 */
export function useMenu() {
    const context = useMenuContext();
    if (!context) {
        return null;
    }
    return {
        focusFirstMenuItem: context.menuItems.focusFirstMenuItem,
    };
}
//# sourceMappingURL=index.js.map